import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Building2, Users, Shield, Server, AlertTriangle, Activity, Eye, Settings, BarChart3, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function PlatformAdmin() {
  const { isDarkMode } = useTheme();
  const [tenants, setTenants] = useState([]);
  const [platformStats, setPlatformStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '',
    subscription_tier: 'free',
    max_users: 10,
    max_assets: 100,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tenantsRes, statsRes] = await Promise.all([
        api.get('/platform/tenants'),
        api.get('/platform/stats')
      ]);
      setTenants(tenantsRes.data);
      setPlatformStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch platform data');
    } finally {
      setLoading(false);
    }
  };

  const viewTenantDetails = async (tenant) => {
    setSelectedTenant(tenant);
    setLoadingDetails(true);
    try {
      const response = await api.get(`/platform/tenants/${tenant.id}`);
      setTenantDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch tenant details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const updateTenant = async (tenantId, updates) => {
    try {
      await api.patch(`/platform/tenants/${tenantId}`, updates);
      toast.success('Tenant updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update tenant');
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/platform/tenants', newTenant);
      toast.success('Tenant created successfully');
      setIsCreateDialogOpen(false);
      setNewTenant({
        name: '',
        subscription_tier: 'free',
        max_users: 10,
        max_assets: 100,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!tenantToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/platform/tenants/${tenantToDelete.id}`);
      toast.success('Tenant deleted successfully');
      setIsDeleteDialogOpen(false);
      setTenantToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteTenant = (tenant) => {
    setTenantToDelete(tenant);
    setIsDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-violet-400 font-rajdhani text-xl">Loading platform data...</div>
      </div>
    );
  }

  return (
    <div data-testid="platform-admin-container" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-rajdhani font-bold text-4xl flex items-center gap-3 ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
            <Building2 className={`w-10 h-10 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
            Platform Administration
          </h1>
          <p className={`font-inter mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Manage all tenants and platform-wide settings</p>
        </div>
        
        {/* Create Tenant Button */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="create-tenant-button"
              className="bg-violet-600 hover:bg-violet-500 font-rajdhani"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className={`${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}`}>
            <DialogHeader>
              <DialogTitle className={`font-rajdhani text-2xl flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Building2 className={`w-6 h-6 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                Create New Tenant
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTenant} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="tenant-name" className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Organization Name</Label>
                <Input
                  id="tenant-name"
                  data-testid="tenant-name-input"
                  value={newTenant.name}
                  onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                  className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  placeholder="Enter organization name"
                  required
                />
              </div>
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Subscription Tier</Label>
                <Select 
                  value={newTenant.subscription_tier} 
                  onValueChange={(value) => setNewTenant({ ...newTenant, subscription_tier: value })}
                >
                  <SelectTrigger data-testid="tenant-tier-select" className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="max-users" className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Max Users</Label>
                  <Input
                    id="max-users"
                    type="number"
                    data-testid="tenant-max-users-input"
                    value={newTenant.max_users}
                    onChange={(e) => setNewTenant({ ...newTenant, max_users: parseInt(e.target.value) || 10 })}
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                    min="1"
                  />
                </div>
                <div>
                  <Label htmlFor="max-assets" className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Max Assets</Label>
                  <Input
                    id="max-assets"
                    type="number"
                    data-testid="tenant-max-assets-input"
                    value={newTenant.max_assets}
                    onChange={(e) => setNewTenant({ ...newTenant, max_assets: parseInt(e.target.value) || 100 })}
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                    min="1"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                data-testid="submit-create-tenant-button"
                disabled={creating}
                className="w-full bg-violet-600 hover:bg-violet-500"
              >
                {creating ? 'Creating...' : 'Create Tenant'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Platform Stats */}
      {platformStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className={`rounded-xl p-4 border-l-4 border-violet-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-2">
              <Building2 className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Total Tenants</span>
            </div>
            <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{platformStats.total_organizations || platformStats.tenants?.total || 0}</p>
            <p className="text-xs text-green-500">{platformStats.total_organizations || platformStats.tenants?.active || 0} active</p>
          </div>
          
          <div className={`rounded-xl p-4 border-l-4 border-blue-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-2">
              <Users className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Total Users</span>
            </div>
            <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{platformStats.total_users || platformStats.users || 0}</p>
          </div>
          
          <div className={`rounded-xl p-4 border-l-4 border-emerald-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-2">
              <Server className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Total Assets</span>
            </div>
            <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{platformStats.total_assets || platformStats.assets || 0}</p>
          </div>
          
          <div className={`rounded-xl p-4 border-l-4 border-orange-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-2">
              <Shield className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Vulnerabilities</span>
            </div>
            <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{platformStats.total_vulnerabilities || platformStats.vulnerabilities?.total || 0}</p>
          </div>
          
          <div className={`rounded-xl p-4 border-l-4 border-red-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Threats</span>
            </div>
            <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{platformStats.total_threats || platformStats.threats?.total || 0}</p>
          </div>
          
          <div className={`rounded-xl p-4 border-l-4 border-indigo-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-2">
              <Activity className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Platform Health</span>
            </div>
            <p className="text-3xl font-rajdhani font-bold text-green-500">98%</p>
          </div>
        </div>
      )}

      {/* Tenants List */}
      <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={`font-rajdhani font-semibold text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>All Tenants</h2>
          <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{tenants.length} organizations</span>
        </div>

        <div className="scroll-container overflow-x-auto">
          <table className="w-full min-w-[700px]" data-testid="tenants-table">
            <thead className={isDarkMode ? 'bg-zinc-900/70' : 'bg-slate-50'}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Organization</th>
                <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Users</th>
                <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Assets</th>
                <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Vulnerabilities</th>
                <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Threats</th>
                <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Status</th>
                <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Tier</th>
                <th className={`px-4 py-3 text-right text-xs font-rajdhani font-semibold uppercase tracking-wider ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-slate-100'}`}>
              {tenants.map((tenant) => (
                <tr key={tenant.id} className={`transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-violet-600/20' : 'bg-violet-100'}`}>
                        <Building2 className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                      </div>
                      <div>
                        <p className={`font-inter text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{tenant.name}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Created: {new Date(tenant.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`font-mono text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{tenant.stats?.users || 0}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`font-mono text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{tenant.stats?.assets || 0}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`font-mono text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>{tenant.stats?.vulnerabilities || 0}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`font-mono text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{tenant.stats?.threats || 0}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      tenant.is_active !== false 
                        ? isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700' 
                        : isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'
                    }`}>
                      {tenant.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded text-xs capitalize ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                      {tenant.subscription_tier || 'free'}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewTenantDetails(tenant)}
                        className={`${isDarkMode ? 'text-indigo-400 hover:bg-indigo-500/10' : 'text-indigo-600 hover:bg-indigo-50'}`}
                        data-testid={`view-tenant-${tenant.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewTenantDetails(tenant)}
                        className={`${isDarkMode ? 'text-zinc-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100'}`}
                        data-testid={`settings-tenant-${tenant.id}`}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => confirmDeleteTenant(tenant)}
                        className={`${isDarkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'}`}
                        data-testid={`delete-tenant-${tenant.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tenant Details Dialog */}
      <Dialog open={!!selectedTenant} onOpenChange={() => setSelectedTenant(null)}>
        <DialogContent className={`max-w-3xl ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}`}>
          <DialogHeader>
            <DialogTitle className={`font-rajdhani text-2xl flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <Building2 className={`w-6 h-6 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
              {selectedTenant?.name}
            </DialogTitle>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <div className={`w-8 h-8 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? 'border-violet-400' : 'border-violet-600'}`}></div>
            </div>
          ) : tenantDetails ? (
            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className={isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="users">Users ({tenantDetails.users?.length || 0})</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`rounded-lg p-4 text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{tenantDetails.stats?.total_users || 0}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Users</p>
                  </div>
                  <div className={`rounded-lg p-4 text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{tenantDetails.stats?.total_assets || 0}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Assets</p>
                  </div>
                  <div className={`rounded-lg p-4 text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>{tenantDetails.stats?.total_vulnerabilities || 0}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Vulnerabilities</p>
                  </div>
                  <div className={`rounded-lg p-4 text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{tenantDetails.stats?.critical_vulnerabilities || 0}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Critical</p>
                  </div>
                </div>

                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                  <h4 className={`font-rajdhani font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Organization Info</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Created:</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{new Date(tenantDetails.created_at).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Status:</span>
                      <span className={`ml-2 ${tenantDetails.is_active !== false ? (isDarkMode ? 'text-green-400' : 'text-green-600') : (isDarkMode ? 'text-red-400' : 'text-red-600')}`}>
                        {tenantDetails.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Subscription:</span>
                      <span className={`ml-2 capitalize ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{tenantDetails.subscription_tier || 'free'}</span>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Threats:</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{tenantDetails.stats?.total_threats || 0}</span>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="users" className="mt-4">
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {tenantDetails.users?.map((user) => (
                    <div key={user.id} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-indigo-600/30' : 'bg-indigo-100'}`}>
                          <Users className={`w-4 h-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                        </div>
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{user.full_name}</p>
                          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{user.email}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs capitalize ${
                        user.role === 'admin' 
                          ? isDarkMode ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-700'
                          : user.role === 'platform_admin' 
                            ? isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'
                            : isDarkMode ? 'bg-zinc-500/20 text-zinc-400' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {user.role}
                      </span>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="settings" className="mt-4 space-y-4">
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Subscription Tier</Label>
                  <Select 
                    value={tenantDetails.subscription_tier || 'free'}
                    onValueChange={(value) => updateTenant(tenantDetails.id, { subscription_tier: value })}
                  >
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-100'}`}>
                  <div>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Active Status</p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Deactivating will block all users</p>
                  </div>
                  <Button
                    variant={tenantDetails.is_active !== false ? "destructive" : "default"}
                    size="sm"
                    onClick={() => updateTenant(tenantDetails.id, { is_active: tenantDetails.is_active === false })}
                  >
                    {tenantDetails.is_active !== false ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}>
          <AlertDialogHeader>
            <AlertDialogTitle className={`font-rajdhani text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Delete Tenant?
            </AlertDialogTitle>
            <AlertDialogDescription className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>
              Are you sure you want to delete <span className={`font-semibold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{tenantToDelete?.name}</span>? 
              This will permanently remove all associated data including users, assets, vulnerabilities, and threats.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDarkMode ? 'bg-zinc-800 text-white border-white/10 hover:bg-zinc-700' : ''}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTenant}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="confirm-delete-tenant-button"
            >
              {deleting ? 'Deleting...' : 'Delete Tenant'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
